<template>
  <z-card width="100%">
    <template #title>
      <h3 v-if="$vuetify.breakpoint.mdAndUp" class="mb-6">
        {{ $t("warrantyRegister.payment_methods") }}
      </h3>

      <p v-else class="body-text text-center mb-6">
        {{ $t("warrantyRegister.required_info") }}
      </p>
    </template>

    <template #content>
      <credit-card-form
        :credit-card="creditCard"
        :billing-address="billingAddress"
        :states="statesOptions.billing"
        :cities="citiesOptions.billing"
        :countries="countries"
        @back="$refs.hotelPayment.openModal()"
        @click:next="onClickPayment"
        @change:card="onChangeCard"
        @change:address="onChangeAddress"
      ></credit-card-form>

      <processing-payment-modal :is-loading="paymentIsLoading" />
      <hotel-payment-container ref="hotelPayment" />
    </template>
  </z-card>
</template>

<script setup>
import find from "lodash.find"
import { computed, onMounted } from "vue"
import { useRouter } from "vue-router/composables"
import {
  useNamespacedActions,
  useNamespacedMutations,
  useNamespacedState,
} from "vuex-composition-helpers"

import { useStoreAction } from "@/composables"
import i18n from "@/i18n"

import CreditCardForm from "./components/CreditCardForm.vue"
import ProcessingPaymentModal from "./components/modals/ProcessingPaymentModal.vue"
import HotelPaymentContainer from "./containers/HotelPaymentContainer.vue"

const router = useRouter()
const { sendNotification } = useNamespacedActions("notifications", [
  "sendNotification",
])
const { countries, statesOptions, citiesOptions } = useNamespacedState(
  "parameters",
  ["statesOptions", "citiesOptions", "countries"]
)

const { guest } = useNamespacedState("guest", ["guest"])
const { billingAddress, creditCard } = useNamespacedState("warranty/payment", [
  "billingAddress",
  "creditCard",
  "order",
])
const { changeBillingAddress, changeCreditCardInfo } = useNamespacedMutations(
  "warranty/payment",
  ["changeBillingAddress", "changeCreditCardInfo"]
)

const { tryFillByZipCode } = useStoreAction(
  "warranty/payment",
  "tryFillByZipCode"
)
const {
  paymentWithoutRegistration,
  isLoading: paymentIsLoading,
  hasError: paymentHasError,
} = useStoreAction("warranty/payment", "paymentWithoutRegistration")

const { getStates } = useStoreAction("parameters", ["getStates"])
const { getCities } = useStoreAction("parameters", ["getCities"])

let isBillingAddressDirty = computed(() => {
  return (
    billingAddress.value.zipCode ||
    billingAddress.value.address ||
    billingAddress.value.neighborhood ||
    billingAddress.value.cityId ||
    billingAddress.value.stateId ||
    billingAddress.value.countryId
  )
})

const onChangeAddress = (payload) => {
  changeBillingAddress(payload)

  if (billingAddress.value.countryId === "BR") {
    if ("zipCode" in payload && payload.zipCode.length === 0) {
      resetBillingAddress()
    }

    if ("zipCode" in payload && payload.zipCode.length === 9) {
      tryFillByZipCode()
    }
  }

  if ("countryId" in payload) onChangeCountry(payload.countryId)
  if ("stateId" in payload) onChangeState(payload.stateId)
  if ("cityId" in payload) onChangeCity(payload.cityId)
}

const onChangeCard = (payload) => {
  changeCreditCardInfo(payload)
}

const onChangeCountry = (countryId) => {
  changeBillingAddress({
    stateName: "",
    stateId: "",
    cityName: "",
    cityId: "",
    neighborhood: "",
    address: "",
    zipCode: "",
  })
  getStates({ section: "billing", countryCode: countryId })
}

const onChangeState = (stateId) => {
  const state = find(statesOptions.value.residence, { id: stateId })

  changeBillingAddress({
    stateName: state?.name ?? "",
    stateId,
    cityId: "",
    cityName: "",
  })

  getCities({
    stateId,
    section: "billing",
  })
}

const onChangeCity = (cityId) => {
  const city = find(citiesOptions.value.residence, { id: cityId })

  changeBillingAddress({
    cityId,
    cityName: city?.name ?? "",
  })
}

const onClickPayment = async () => {
  await paymentWithoutRegistration()

  if (paymentHasError.value) {
    sendNotification({
      message: i18n.tc("error.generic_error"),
      duration: 2000,
      confirmationText: "Ok",
      color: "red",
    })
  } else {
    router.push({ name: "processing-payment" })
  }
}

const fillBillingAddressWithGuestInfo = () => {
  let fields = [
    "zipCode",
    "address",
    "addressNumber",
    "addressComplement",
    "neighborhood",
    "cityId",
    "cityName",
    "stateName",
    "stateId",
    "countryId",
    "email",
  ]

  if (guest.value.countryId && guest.value.stateId) {
    getStates({ countryCode: guest.value.countryId, section: "billing" })
    getCities({ stateId: guest.value.stateId, section: "billing" })
  }

  for (let field of fields) {
    changeBillingAddress({ [field]: guest.value[field] || "" })
  }
}

const resetBillingAddress = () => {
  changeBillingAddress({
    zipCode: "",
    address: "",
    addressNumber: "",
    addressComplement: "",
    neighborhood: "",
    cityId: "",
    stateId: "",
  })
}

onMounted(() => {
  if (!isBillingAddressDirty.value) {
    fillBillingAddressWithGuestInfo()
  }
})
</script>
